import type { CSSProperties } from 'react';
import type { VideoViewCrop } from './types';

export interface RectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CropPoint {
  x: number;
  y: number;
}

export type VideoCropResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const FULL_FRAME_VIDEO_VIEW_CROP: VideoViewCrop = {
  x: 0,
  y: 0,
  width: 1,
  height: 1
};

export const DEFAULT_VIDEO_VIEW_CROP_SELECTION: VideoViewCrop = {
  x: 0.1,
  y: 0.1,
  width: 0.8,
  height: 0.8
};

export const MIN_VIDEO_VIEW_CROP_SIZE = 0.04;

const clamp = (value: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
};

const roundCropNumber = (value: number): number => (
  Number.parseFloat(value.toFixed(6))
);

export const getMediaContentRect = (
  containerRect: RectLike,
  intrinsicAspectRatio?: number | null
): RectLike => {
  const { left, top, width, height } = containerRect;

  if (width <= 0 || height <= 0) {
    return { left, top, width: Math.max(width, 0), height: Math.max(height, 0) };
  }

  if (!intrinsicAspectRatio || !Number.isFinite(intrinsicAspectRatio) || intrinsicAspectRatio <= 0) {
    return { left, top, width, height };
  }

  const containerAspectRatio = width / height;

  if (intrinsicAspectRatio > containerAspectRatio) {
    const contentHeight = width / intrinsicAspectRatio;
    return {
      left,
      top: top + ((height - contentHeight) / 2),
      width,
      height: contentHeight
    };
  }

  const contentWidth = height * intrinsicAspectRatio;
  return {
    left: left + ((width - contentWidth) / 2),
    top,
    width: contentWidth,
    height
  };
};

export const getContentPointFromClientPoint = (
  clientX: number,
  clientY: number,
  contentRect: RectLike
): CropPoint => {
  if (contentRect.width <= 0 || contentRect.height <= 0) {
    return { x: 0, y: 0 };
  }

  return {
    x: clamp((clientX - contentRect.left) / contentRect.width, 0, 1),
    y: clamp((clientY - contentRect.top) / contentRect.height, 0, 1)
  };
};

export const clampVideoViewCrop = (
  crop: VideoViewCrop,
  minSize = MIN_VIDEO_VIEW_CROP_SIZE
): VideoViewCrop => {
  const safeMinSize = clamp(minSize, 0.001, 1);
  const candidate = (
    crop &&
    Number.isFinite(crop.x) &&
    Number.isFinite(crop.y) &&
    Number.isFinite(crop.width) &&
    Number.isFinite(crop.height) &&
    crop.width > 0 &&
    crop.height > 0
  )
    ? crop
    : DEFAULT_VIDEO_VIEW_CROP_SELECTION;
  const width = clamp(candidate.width, safeMinSize, 1);
  const height = clamp(candidate.height, safeMinSize, 1);
  const x = clamp(candidate.x, 0, 1 - width);
  const y = clamp(candidate.y, 0, 1 - height);

  return {
    x: roundCropNumber(x),
    y: roundCropNumber(y),
    width: roundCropNumber(width),
    height: roundCropNumber(height)
  };
};

export const getVideoViewCropFromPoints = (
  startPoint: CropPoint,
  endPoint: CropPoint,
  minSize = MIN_VIDEO_VIEW_CROP_SIZE
): VideoViewCrop => {
  const safeMinSize = clamp(minSize, 0.001, 1);
  const startX = clamp(startPoint.x, 0, 1);
  const startY = clamp(startPoint.y, 0, 1);
  const endX = clamp(endPoint.x, 0, 1);
  const endY = clamp(endPoint.y, 0, 1);
  let left = Math.min(startX, endX);
  let top = Math.min(startY, endY);
  let width = Math.abs(endX - startX);
  let height = Math.abs(endY - startY);

  if (width < safeMinSize) {
    left = endX < startX
      ? clamp(startX - safeMinSize, 0, 1 - safeMinSize)
      : clamp(startX, 0, 1 - safeMinSize);
    width = safeMinSize;
  }

  if (height < safeMinSize) {
    top = endY < startY
      ? clamp(startY - safeMinSize, 0, 1 - safeMinSize)
      : clamp(startY, 0, 1 - safeMinSize);
    height = safeMinSize;
  }

  return clampVideoViewCrop({ x: left, y: top, width, height }, safeMinSize);
};

export const getVisualCropFromContentCrop = (
  crop: VideoViewCrop,
  isMirrored: boolean
): VideoViewCrop => {
  const normalizedCrop = clampVideoViewCrop(crop);

  if (!isMirrored) return normalizedCrop;

  return {
    ...normalizedCrop,
    x: roundCropNumber(1 - normalizedCrop.x - normalizedCrop.width)
  };
};

export const getContentCropFromVisualCrop = (
  crop: VideoViewCrop,
  isMirrored: boolean
): VideoViewCrop => {
  const normalizedCrop = clampVideoViewCrop(crop);

  if (!isMirrored) return normalizedCrop;

  return clampVideoViewCrop({
    ...normalizedCrop,
    x: roundCropNumber(1 - normalizedCrop.x - normalizedCrop.width)
  });
};

export const moveVideoViewCrop = (
  crop: VideoViewCrop,
  deltaX: number,
  deltaY: number
): VideoViewCrop => {
  const normalizedCrop = clampVideoViewCrop(crop);

  return clampVideoViewCrop({
    ...normalizedCrop,
    x: normalizedCrop.x + deltaX,
    y: normalizedCrop.y + deltaY
  });
};

export const resizeVideoViewCrop = (
  crop: VideoViewCrop,
  handle: VideoCropResizeHandle,
  point: CropPoint,
  minSize = MIN_VIDEO_VIEW_CROP_SIZE
): VideoViewCrop => {
  const normalizedCrop = clampVideoViewCrop(crop, minSize);
  const safeMinSize = clamp(minSize, 0.001, 1);
  let left = normalizedCrop.x;
  let right = normalizedCrop.x + normalizedCrop.width;
  let top = normalizedCrop.y;
  let bottom = normalizedCrop.y + normalizedCrop.height;

  if (handle.includes('w')) {
    left = clamp(point.x, 0, right - safeMinSize);
  }
  if (handle.includes('e')) {
    right = clamp(point.x, left + safeMinSize, 1);
  }
  if (handle.includes('n')) {
    top = clamp(point.y, 0, bottom - safeMinSize);
  }
  if (handle.includes('s')) {
    bottom = clamp(point.y, top + safeMinSize, 1);
  }

  return clampVideoViewCrop({
    x: left,
    y: top,
    width: right - left,
    height: bottom - top
  }, safeMinSize);
};

export const getVideoViewCropStyle = (crop: VideoViewCrop): CSSProperties => {
  const normalizedCrop = clampVideoViewCrop(crop);

  return {
    left: `${roundCropNumber(normalizedCrop.x * 100)}%`,
    top: `${roundCropNumber(normalizedCrop.y * 100)}%`,
    width: `${roundCropNumber(normalizedCrop.width * 100)}%`,
    height: `${roundCropNumber(normalizedCrop.height * 100)}%`
  };
};
